import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: 'https://backend.hellolynx.io/dashboard/',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
export const tokenManager = {
  getToken: () => localStorage.getItem('authToken'),
  setToken: (token) => localStorage.setItem('authToken', token),
  removeToken: () => localStorage.removeItem('authToken'),
  isAuthenticated: () => !!localStorage.getItem('authToken'),
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      tokenManager.removeToken();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API methods
export const apiClient = {
  // Dashboard Statistics
  getDashboardStats: async () => {
    try {
      const response = await api.get("dashboard");
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch dashboard statistics');
    }
  },

  // Authentication
  login: async (credentials) => {
    try {
      const response = await api.post('login', credentials);
      // Handle the response structure: { message, data: { user, token } }
      const { data } = response.data;
      const { token, user } = data;
      tokenManager.setToken(token);
      return { token, user };
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  },

  logout: () => {
    tokenManager.removeToken();
    window.location.href = '/login';
  },

  // Generic CRUD operations
  get: async (endpoint, params = {}) => {
    const response = await api.get(endpoint, { params });
    return response.data;
  },

  // Transactions specific method
  getTransactions: async (page = 1, limit = 10, search = '', email = '') => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', limit);
      if (search) queryParams.append('search', search);
      if (email) queryParams.append('email', email);
      
      const response = await api.get(`transactions?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch transactions');
    }
  },

  // Contact Us methods
  getContactUs: async (page = 1, limit = 10) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', limit);
      
      const response = await api.get(`contact-us?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch contact us messages');
    }
  },

  getContactUsById: async (id) => {
    try {
      const response = await api.get(`contact-us/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch contact us message details');
    }
  },

  approveContactUs: async (id) => {
    try {
      const response = await api.put(`contact-us/${id}/accept`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to approve contact us message');
    }
  },

  rejectContactUs: async (id) => {
    try {
      const response = await api.put(`contact-us/${id}/reject`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reject contact us message');
    }
  },

  // Companies dropdown
  getCompaniesDropdown: async () => {
    try {
      const response = await api.get('companies/dropdown');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch companies dropdown');
    }
  },

  post: async (endpoint, data) => {
    const response = await api.post(endpoint, data);
    return response.data;
  },

  put: async (endpoint, data) => {
    const response = await api.put(endpoint, data);
    return response.data;
  },

  delete: async (endpoint) => {
    const response = await api.delete(endpoint);
    return response.data;
  },

  // FormData upload method
  postFormData: async (endpoint, formData) => {
    const response = await api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // PUT with FormData
  putFormData: async (endpoint, formData) => {
    const response = await api.put(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Paginated data fetching
  getPaginated: async (endpoint, page = 1, limit = 10, filters = {}) => {
    const params = {
      _page: page,
      _limit: limit,
      ...filters,
    };
    const response = await api.get(endpoint, { params });
    
    // Get total count from headers (common pattern)
    const total = parseInt(response.headers['x-total-count'] || response.data.length);
    
    return {
      data: response.data,
      pagination: {
        current: page,
        pageSize: limit,
        total,
        totalPages: Math.ceil(total / limit),
      },
    };
  },

  // Admin Management APIs
  admins: {
    // Get paginated list of admins
    getAll: async (page = 1, limit = 10) => {
      try {
        const response = await api.get('admins', {
          params: { page, limit }
        });
        return {
          admins: response.data.admins || response.data,
          total: response.data.total || response.data.length,
        };
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch admins');
      }
    },

    // Get single admin by ID
    getById: async (id) => {
      try {
        const response = await api.get(`admins/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch admin details');
      }
    },

    // Create new admin
    create: async (adminData) => {
      try {
        const formData = new FormData();
        formData.append('name', adminData.name);
        formData.append('email', adminData.email);
        formData.append('password', adminData.password);
        if (adminData.image) {
          formData.append('image', adminData.image);
        }

        const response = await api.post('admins', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to create admin');
      }
    },

    // Update admin
    update: async (id, adminData) => {
      try {
        const formData = new FormData();
        formData.append('name', adminData.name);
        formData.append('email', adminData.email);
        if (adminData.password) {
          formData.append('password', adminData.password);
        }
        if (adminData.image) {
          formData.append('image', adminData.image);
        }
        formData.append('_method', 'PUT');

        const response = await api.post(`admins/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update admin');
      }
    },

    // Delete admin
    delete: async (id) => {
      try {
        const response = await api.delete(`admins/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to delete admin');
      }
    },
  },

  // CMS Pages Management APIs
  cmsPages: {
    // Get Terms page
    getTerms: async () => {
      try {
        const response = await api.get('cms-pages/terms');
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch terms page');
      }
    },

    // Update Terms page
    updateTerms: async (data) => {
      try {
        const response = await api.put('cms-pages/terms', data);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update terms page');
      }
    },

    // Get Privacy Policy page
    getPrivacy: async () => {
      try {
        const response = await api.get('cms-pages/policy');
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch privacy policy page');
      }
    },

    // Update Privacy Policy page
    updatePrivacy: async (data) => {
      try {
        const response = await api.put('cms-pages/policy', data);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update privacy policy page');
      }
    },

    // Get FAQs page
    getFaqs: async () => {
      try {
        const response = await api.get('cms-pages/faq');
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch FAQs page');
      }
    },

    // Update FAQs page
    updateFaqs: async (data) => {
      try {
        const response = await api.put('cms-pages/faq', data);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update FAQs page');
      }
    },

    // Get About page
    getAbout: async () => {
      try {
        const response = await api.get('page-data/about-us');
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch about page');
      }
    },

    // Update About page
    updateAbout: async (data) => {
      try {
        const response = await api.post('page-data/about-us', data);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update about page');
      }
    },

    // Get Settings page
    getSettings: async () => {
      try {
        const response = await api.get('page-data/settings');
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch settings page');
      }
    },

    // Update Settings page
    updateSettings: async (data) => {
      try {
        const response = await api.post('page-data/settings', data);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update settings page');
      }
    },
  },

  // Content Management APIs
  content: {
    getSystemImages: async () => {
      try {
        const response = await api.get('system-images');
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch system images');
      }
    },

    updateSystemImage: async (id, imageFile) => {
      try {
        const formData = new FormData();
        formData.append('_method', 'PUT');
        formData.append('value', imageFile);

        const response = await api.post(`system-images/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update system image');
      }
    },
  },
  
  // Interests Management APIs
  interests: {
    // Get paginated list of interests
    getAll: async (page = 1, limit = 10) => {
      try {
        const response = await api.get('interests', {
          params: { page, limit }
        });
        return {
          interests: response.data.interests || response.data.data || response.data,
          total: response.data.total || response.data.length,
        };
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch interests');
      }
    },

    // Get single interest by ID
    getById: async (id) => {
      try {
        const response = await api.get(`interests/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch interest details');
      }
    },

    // Create new interest
    create: async (interestData) => {
      try {
        const response = await api.post('interests', interestData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to create interest');
      }
    },

    // Update interest
    update: async (id, interestData) => {
      try {
        const response = await api.put(`interests/${id}`, interestData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update interest');
      }
    },

    // Delete interest
    delete: async (id) => {
      try {
        const response = await api.delete(`interests/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to delete interest');
      }
    },
  },
  
  // Nationalities Management APIs
  nationalities: {
    // Get paginated list of nationalities
    getAll: async (page = 1, limit = 10) => {
      try {
        const response = await api.get('nationalities', {
          params: { page, limit }
        });
        return {
          nationalities: response.data.nationalities || response.data.data || response.data,
          total: response.data.total || response.data.length,
        };
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch nationalities');
      }
    },

    // Get single nationality by ID
    getById: async (id) => {
      try {
        const response = await api.get(`nationalities/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch nationality details');
      }
    },

    // Create new nationality
    create: async (nationalityData) => {
      try {
        const response = await api.post('nationalities', nationalityData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to create nationality');
      }
    },

    // Update nationality
    update: async (id, nationalityData) => {
      try {
        const response = await api.put(`nationalities/${id}`, nationalityData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update nationality');
      }
    },

    // Delete nationality
    delete: async (id) => {
      try {
        const response = await api.delete(`nationalities/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to delete nationality');
      }
    },
  },
  
  // Student Management APIs
  students: {
    // Get paginated list of students with search
    getAll: async (page = 1, limit = 10, search = '') => {
      try {
        const params = { page, limit };
        if (search) {
          params.search = search; // For name search functionality
        }
        
        const response = await api.get('/how-to-apply/student/data', { params });
        return {
          students: response.data.data || response.data.students || response.data,
          total: response.data.total || response.data.length,
          pagination: response.data.pagination || {
            current: page,
            pageSize: limit,
            total: response.data.total || response.data.length,
            totalPages: Math.ceil((response.data.total || response.data.length) / limit)
          }
        };
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch students');
      }
    },

    // Get single student by ID
    getById: async (id) => {
      try {
        const response = await api.get(`/how-to-apply/student/data/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch student details');
      }
    },

    // Create new student
    create: async (studentData) => {
      try {
        const response = await api.post('/how-to-apply/student/data', studentData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to create student');
      }
    },

    // Update student
    update: async (id, studentData) => {
      try {
        const response = await api.put(`/how-to-apply/student/data/${id}`, studentData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update student');
      }
    },

    // Delete student
    delete: async (id) => {
      try {
        const response = await api.delete(`/how-to-apply/student/data/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to delete student');
      }
    },
  },

  // Home Banners Management APIs
  homeBanners: {
    // Get paginated list of home banners
    getAll: async (page = 1, limit = 10) => {
      try {
        const response = await api.get('home-banners', {
          params: { page, limit }
        });
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch home banners');
      }
    },

    // Get single home banner by ID
    getById: async (id) => {
      try {
        const response = await api.get(`home-banners/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch home banner details');
      }
    },

    // Create new home banner
    create: async (bannerData) => {
      try {
        const response = await api.post('home-banners', bannerData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to create home banner');
      }
    },

    // Update home banner
    update: async (id, bannerData) => {
      try {
        const response = await api.put(`home-banners/${id}`, bannerData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update home banner');
      }
    },

    // Delete home banner
    delete: async (id) => {
      try {
        const response = await api.delete(`home-banners/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to delete home banner');
      }
    },
  },

  // What We Are Management APIs
  whatWeAre: {
    // Get what we are data
    get: async () => {
      try {
        const response = await api.get('what-we-offer/home-page');
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch what we are data');
      }
    },

    // Create what we are data
    create: async (data) => {
      try {
        const response = await api.put('what-we-offer/home-page', data);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to create what we are data');
      }
    },

    // Update what we are data
    update: async (data) => {
      try {
        const response = await api.put(`what-we-offer/home-page`, data);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update what we are data');
      }
    },

    // Delete what we are data
    delete: async (id) => {
      try {
        const response = await api.delete(`what-we-offer/home-page/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to delete what we are data');
      }
    },
  },

  // Companies Management APIs
  companies: {
    // Get paginated list of companies with search
    getAll: async (page = 1, limit = 10, search = '') => {
      try {
        const params = { page, limit };
        if (search) {
          params.search = search;
        }
        
        const response = await api.get('companies', { params });
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch companies');
      }
    },

    // Get single company by ID
    getById: async (id) => {
      try {
        const response = await api.get(`companies/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch company details');
      }
    },
  },

  // Subscriptions Management APIs
  subscriptions: {
    // Get paginated list of subscriptions
    getAll: async (page = 1, limit = 10) => {
      try {
        const response = await api.get('subscriptions', {
          params: { page, limit }
        });
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch subscriptions');
      }
    },

    // Get single subscription by ID
    getById: async (id) => {
      try {
        const response = await api.get(`subscriptions/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to fetch subscription details');
      }
    },

    // Create new subscription
    create: async (subscriptionData) => {
      try {
        const response = await api.post('subscriptions', subscriptionData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to create subscription');
      }
    },

    // Update subscription
    update: async (id, subscriptionData) => {
      try {
        const response = await api.put(`subscriptions/${id}`, subscriptionData);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to update subscription');
      }
    },

    // Delete subscription
    delete: async (id) => {
      try {
        const response = await api.delete(`subscriptions/${id}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to delete subscription');
      }
    },
  },

  // Subscriptions dropdown
  getSubscriptionsDropdown: async () => {
    try {
      const response = await api.get('subscriptions/dropdown');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch subscriptions dropdown');
    }
  },

  // Users dropdown
  getUsersDropdown: async (query = '') => {
    try {
      const config = query ? { params: { search: query } } : {};
      const response = await api.get('companies/dropdown', config);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch users dropdown');
    }
  },
};

export default api; 